import React, {useEffect, useState, useCallback, useRef, useMemo} from 'react';
import {connect} from 'react-redux';
import {isMobile} from 'react-device-detect';
import {getNumberTypePrice, setSelectedNumberType, getNumbersByType, setCurrentStep, saveToHistory} from '../changePhoneNumberSlice';
import SelectOptionButton from 'app/Components/Shared/SelectOptionButton/SelectOptionButton';
import NewNumbersData from './PartialComponents/NewNumbersData';
import NewNumbersLoading from './PartialComponents/NewNumbersLoading';
import NewNumberDataError from './PartialComponents/NewNumberDataError';
import NewNumberRefreshButton from './PartialComponents/NewNumberRefreshButton';
import BtnSubmit from 'app/Components/Shared/Buttons/BtnSubmit';
import {ChangePhoneNumberStep} from '../Constant/Constant';

const SelectNewNumber = (props) => {
  const [error, setError] = useState(false);
  const [continueButtonDisabled, setContinueButtonDisabled] = useState(true);
  const lastSelectedTypeRef = useRef(null);
  const numbersDataRef = useRef(null);
  const prevLoadingState = useRef(false);

  useEffect(() => {
    if (props.changePhoneNumber.newNumbersData?.length > 0 && !props.changePhoneNumber.getNumberPricesCompleted) {
      props.getNumberTypePrice(props.changePhoneNumber.selectedAccount);
    }
  }, []);

  useEffect(() => {
    const isLoading = props.changePhoneNumber.newNumbersDataLoading;
    const wasLoading = prevLoadingState.current;
    const selectedType = props.changePhoneNumber.newNumbersData.find((numberType) => numberType.selected);
    const hasNumbers = selectedType?.numbers && selectedType.numbers.length > 0;
    const hasError = props.changePhoneNumber.newNumbersDataError;

    if (wasLoading && !isLoading && numbersDataRef.current) {
      if (hasNumbers || hasError) {
        requestAnimationFrame(() => {
          numbersDataRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });

          setTimeout(() => {
            numbersDataRef.current?.focus({preventScroll: true});
          }, 100);
        });
      }
    }

    prevLoadingState.current = isLoading;
  }, [props.changePhoneNumber.newNumbersDataLoading, props.changePhoneNumber.newNumbersData, props.changePhoneNumber.newNumbersDataError]);

  // Handle fetching numbers when a type is selected
  useEffect(() => {
    const selectedType = props.changePhoneNumber.newNumbersData.find((numberType) => numberType.selected);

    if (selectedType && selectedType.code !== lastSelectedTypeRef.current) {
      lastSelectedTypeRef.current = selectedType.code;
      if (selectedType.numbers.length == 0 && !props.changePhoneNumber.newNumbersDataLoading) {
        props.getNumbersByType(selectedType.code);
      }
    }
  }, [props.changePhoneNumber.newNumbersData, props.changePhoneNumber.newNumbersDataLoading]);

  // Create a mutable copy of typesData from Redux (Redux state is immutable)
  const typesData = useMemo(() => {
    return props.changePhoneNumber.typesData.map((type) => ({...type}));
  }, [props.changePhoneNumber.typesData]);

  // Memoized callback to prevent unnecessary re-renders of child components
  const handleTypeSelection = useCallback(
    (type) => {
      const typeData = props.changePhoneNumber.newNumbersData.find((numType) => numType.code === type);
      if (typeData && !typeData.selected) {
        setError(false); // Reset error state when changing selection
        props.setSelectedNumberType(type);
      }
    },
    [props.changePhoneNumber.newNumbersData, props.setSelectedNumberType],
  );

  const validateAndContinue = async () => {
    setError(false);
    if (props.changePhoneNumber.newNumbersData.find((numberType) => numberType.selected) && props.changePhoneNumber.newSelectedNumber) {
      await props.saveToHistory(props.changePhoneNumber.currentStep);
      props.setCurrentStep(ChangePhoneNumberStep.CheckoutSummary);
    } else {
      setError(true);
    }
  };

  // Memoized view component to prevent unnecessary re-renders
  const numbersView = useMemo(() => {
    const selectedType = props.changePhoneNumber.newNumbersData.find((numberType) => numberType.selected);

    if (!selectedType) {
      return (
        <>
          <NewNumbersData setError={setError} disabled={true} />
          <NewNumberRefreshButton disabled={true} />
        </>
      );
    } else {
      setContinueButtonDisabled(false);
      if (props.changePhoneNumber.newNumbersDataLoading) {
        return <NewNumbersLoading />;
      }

      if (props.changePhoneNumber.newNumbersDataError) {
        return (
          <>
            <NewNumberDataError ref={numbersDataRef} />
            <NewNumberRefreshButton />
          </>
        );
      }

      // Check if this type has any fetched numbers
      if (selectedType.numbers && selectedType.numbers.length > 0) {
        return (
          <>
            <NewNumbersData setError={setError} ref={numbersDataRef} />
            <NewNumberRefreshButton />
          </>
        );
      }
    }

    return null;
  }, [props.changePhoneNumber.newNumbersData, props.changePhoneNumber.newNumbersDataLoading, props.changePhoneNumber.newNumbersDataError]);

  return (
    <div className='step select_new-number'>
      {props.changePhoneNumber.getNumberPricesCompleted && (
        <div>
          <div className='select-type'>
            <div className={`l-title ${isMobile ? 'right' : ''}`}> לאיזה סוג מספר להחליף? </div>
            <SelectOptionButton onClick={handleTypeSelection} buttonsData={typesData} />
          </div>

          <div>{numbersView}</div>
          <div className={`continue-button ${continueButtonDisabled ? 'disabled' : ''}`}>
            {error && <div className='error center'>כדי להמשיך יש לבחור מספר </div>}
            <BtnSubmit onclick={() => validateAndContinue()} text={'המשך'} disabled={continueButtonDisabled} />
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  changePhoneNumber: state.changePhoneNumber,
});

const mapDispatchToProps = {
  getNumberTypePrice,
  setSelectedNumberType,
  getNumbersByType,
  setCurrentStep,
  saveToHistory,
};

export default connect(mapStateToProps, mapDispatchToProps)(SelectNewNumber);
